import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Network, Search, Server, Loader2, Copy, RefreshCw } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface DNSRecord {
  type: string;
  name: string;
  value: string;
  ttl?: number;
  priority?: number;
}

interface DNSResults {
  domain: string;
  records: DNSRecord[];
  queryTime: number;
  nameserver?: string;
}

const DNSLookupTool: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [recordType, setRecordType] = useState('A');
  const [dnsResults, setDnsResults] = useState<DNSResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useSEO({
    title: 'DNS Lookup Tool - Check DNS Records for Any Domain',
    description: 'Perform DNS lookups to check A, AAAA, CNAME, MX, TXT, NS, SOA records and more. Analyze DNS configuration and troubleshoot domain issues.',
    keywords: 'DNS lookup, DNS records, A record, AAAA record, CNAME, MX record, TXT record, NS record, SOA record, DNS troubleshooting',
    canonical: 'https://snap-tools.online/security/dns-lookup-tool'
  });

  // Available DNS record types
  const recordTypes = [
    { value: 'A', label: 'A - IPv4 Address', description: 'Maps domain to IPv4 address' },
    { value: 'AAAA', label: 'AAAA - IPv6 Address', description: 'Maps domain to IPv6 address' },
    { value: 'CNAME', label: 'CNAME - Canonical Name', description: 'Alias pointing to another domain' },
    { value: 'MX', label: 'MX - Mail Exchange', description: 'Mail server for domain' },
    { value: 'TXT', label: 'TXT - Text Record', description: 'Arbitrary text data' },
    { value: 'NS', label: 'NS - Name Server', description: 'Authoritative name servers' },
    { value: 'SOA', label: 'SOA - Start of Authority', description: 'Administrative information' },
    { value: 'PTR', label: 'PTR - Pointer', description: 'Reverse DNS lookup' },
    { value: 'SRV', label: 'SRV - Service', description: 'Service location record' },
    { value: 'CAA', label: 'CAA - Certificate Authority', description: 'SSL certificate authority authorization' }
  ];

  // Validate domain format
  const isValidDomain = (domainName: string): boolean => {
    const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    return domainRegex.test(domainName);
  };

  // Clean domain input
  const cleanDomain = (input: string): string => {
    let cleaned = input.trim().toLowerCase();
    // Remove protocol if present
    cleaned = cleaned.replace(/^https?:\/\//, '');
    // Remove www if present for most record types (except for specific lookups)
    if (recordType !== 'CNAME') {
      cleaned = cleaned.replace(/^www\./, '');
    }
    // Remove trailing slash
    cleaned = cleaned.replace(/\/$/, '');
    // Extract domain part if URL path is present
    cleaned = cleaned.split('/')[0];
    return cleaned;
  };

  // Generate mock DNS data for demonstration
  const generateMockDNSData = (domainName: string, type: string): DNSRecord[] => {
    const records: DNSRecord[] = [];

    switch (type) {
      case 'A':
        records.push(
          { type: 'A', name: domainName, value: '93.184.216.34', ttl: 3600 },
          { type: 'A', name: domainName, value: '93.184.216.35', ttl: 3600 }
        );
        break;
      case 'AAAA':
        records.push(
          { type: 'AAAA', name: domainName, value: '2606:2800:220:1:248:1893:25c8:1946', ttl: 3600 }
        );
        break;
      case 'CNAME':
        if (domainName.startsWith('www.')) {
          records.push(
            { type: 'CNAME', name: domainName, value: domainName.replace('www.', ''), ttl: 3600 }
          );
        } else {
          records.push(
            { type: 'CNAME', name: `www.${domainName}`, value: domainName, ttl: 3600 }
          );
        }
        break;
      case 'MX':
        records.push(
          { type: 'MX', name: domainName, value: 'mail.example.com', ttl: 3600, priority: 10 },
          { type: 'MX', name: domainName, value: 'mail2.example.com', ttl: 3600, priority: 20 }
        );
        break;
      case 'TXT':
        records.push(
          { type: 'TXT', name: domainName, value: 'v=spf1 include:_spf.google.com ~all', ttl: 3600 },
          { type: 'TXT', name: domainName, value: 'google-site-verification=AbCdEfGhIjKlMnOpQrStUvWxYz', ttl: 3600 }
        );
        break;
      case 'NS':
        records.push(
          { type: 'NS', name: domainName, value: 'ns1.example.com', ttl: 86400 },
          { type: 'NS', name: domainName, value: 'ns2.example.com', ttl: 86400 }
        );
        break;
      case 'SOA':
        records.push(
          { 
            type: 'SOA', 
            name: domainName, 
            value: 'ns1.example.com hostmaster.example.com 2023120101 3600 1800 604800 86400', 
            ttl: 86400 
          }
        );
        break;
      case 'CAA':
        records.push(
          { type: 'CAA', name: domainName, value: '0 issue "letsencrypt.org"', ttl: 3600 },
          { type: 'CAA', name: domainName, value: '0 iodef "mailto:security@example.com"', ttl: 3600 }
        );
        break;
      default:
        records.push(
          { type: type, name: domainName, value: 'No records found', ttl: 0 }
        );
    }

    return records;
  };

  // Perform DNS lookup
  const performDNSLookup = async () => {
    const cleanedDomain = cleanDomain(domain);
    
    if (!cleanedDomain) {
      toast.error('Please enter a domain name');
      return;
    }

    if (!isValidDomain(cleanedDomain)) {
      toast.error('Please enter a valid domain name');
      return;
    }

    setIsLoading(true);
    setDnsResults(null);

    try {
      const startTime = Date.now();
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const endTime = Date.now();
      const queryTime = endTime - startTime;

      // Generate mock DNS data
      const records = generateMockDNSData(cleanedDomain, recordType);

      const results: DNSResults = {
        domain: cleanedDomain,
        records,
        queryTime,
        nameserver: '8.8.8.8'
      };

      setDnsResults(results);
      toast.success(`DNS lookup completed in ${queryTime}ms`);
    } catch (error) {
      console.error('Error performing DNS lookup:', error);
      toast.error('Failed to perform DNS lookup. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Lookup all record types
  const lookupAllRecords = async () => {
    const cleanedDomain = cleanDomain(domain);
    
    if (!cleanedDomain) {
      toast.error('Please enter a domain name');
      return;
    }

    if (!isValidDomain(cleanedDomain)) {
      toast.error('Please enter a valid domain name');
      return;
    }

    setIsLoading(true);
    setDnsResults(null);

    try {
      const startTime = Date.now();
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const endTime = Date.now();
      const queryTime = endTime - startTime;

      // Generate records for all types
      const allRecords: DNSRecord[] = [];
      const typesToCheck = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS'];
      
      typesToCheck.forEach(type => {
        const records = generateMockDNSData(cleanedDomain, type);
        allRecords.push(...records);
      });

      const results: DNSResults = {
        domain: cleanedDomain,
        records: allRecords,
        queryTime,
        nameserver: '8.8.8.8'
      };

      setDnsResults(results);
      toast.success(`Complete DNS lookup finished in ${queryTime}ms`);
    } catch (error) {
      console.error('Error performing complete DNS lookup:', error);
      toast.error('Failed to perform complete DNS lookup. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Copy record to clipboard
  const copyRecord = async (record: DNSRecord) => {
    const recordText = `${record.name} ${record.ttl} IN ${record.type} ${record.priority ? record.priority + ' ' : ''}${record.value}`;
    try {
      await navigator.clipboard.writeText(recordText);
      toast.success('DNS record copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy record');
    }
  };

  // Copy all records
  const copyAllRecords = async () => {
    if (!dnsResults) return;
    
    const allRecordsText = dnsResults.records
      .map(record => `${record.name} ${record.ttl} IN ${record.type} ${record.priority ? record.priority + ' ' : ''}${record.value}`)
      .join('\n');
    
    try {
      await navigator.clipboard.writeText(allRecordsText);
      toast.success('All DNS records copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy records');
    }
  };

  // Get record type color
  const getRecordTypeColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      'A': 'bg-blue-100 text-blue-800',
      'AAAA': 'bg-indigo-100 text-indigo-800',
      'CNAME': 'bg-green-100 text-green-800',
      'MX': 'bg-red-100 text-red-800',
      'TXT': 'bg-yellow-100 text-yellow-800',
      'NS': 'bg-purple-100 text-purple-800',
      'SOA': 'bg-gray-100 text-gray-800',
      'PTR': 'bg-orange-100 text-orange-800',
      'SRV': 'bg-pink-100 text-pink-800',
      'CAA': 'bg-teal-100 text-teal-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Server className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">DNS Lookup Tool</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Perform DNS lookups to check various record types for any domain. Analyze DNS configuration, troubleshoot issues, and verify domain settings.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* DNS Lookup Form */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Domain Name:
                  </label>
                  <input
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="Enter domain name (e.g., example.com)"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && performDNSLookup()}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Record Type:
                  </label>
                  <select
                    value={recordType}
                    onChange={(e) => setRecordType(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {recordTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={performDNSLookup}
                  disabled={isLoading || !domain.trim()}
                  className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  Lookup {recordType} Records
                </button>
                
                <button
                  onClick={lookupAllRecords}
                  disabled={isLoading || !domain.trim()}
                  className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Lookup All Records
                </button>
              </div>
            </div>
          </div>

          {/* Record Type Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-blue-900 mb-3">Selected Record Type: {recordType}</h3>
            <p className="text-blue-800 text-sm">
              {recordTypes.find(type => type.value === recordType)?.description}
            </p>
          </div>

          {/* DNS Results */}
          {dnsResults && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-lg p-6 mb-6"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Server className="w-5 h-5 text-gray-700" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    DNS Results for {dnsResults.domain}
                  </h3>
                </div>
                <button
                  onClick={copyAllRecords}
                  className="flex items-center gap-1 text-purple-600 hover:text-purple-700 text-sm"
                >
                  <Copy className="w-4 h-4" />
                  Copy All
                </button>
              </div>

              {/* Query Information */}
              <div className="bg-white rounded-lg p-4 mb-4">
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Query Time:</span>
                    <span className="ml-2 font-medium">{dnsResults.queryTime}ms</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Nameserver:</span>
                    <span className="ml-2 font-medium">{dnsResults.nameserver}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Records Found:</span>
                    <span className="ml-2 font-medium">{dnsResults.records.length}</span>
                  </div>
                </div>
              </div>

              {/* DNS Records */}
              <div className="space-y-3">
                {dnsResults.records.map((record, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-lg p-4 border"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getRecordTypeColor(record.type)}`}>
                          {record.type}
                        </span>
                        <span className="font-medium text-gray-900">{record.name}</span>
                      </div>
                      <button
                        onClick={() => copyRecord(record)}
                        className="text-gray-400 hover:text-gray-600"
                        title="Copy record"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-4 flex-wrap">
                        <div>
                          <span className="text-gray-600">Value:</span>
                          <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">
                            {record.value}
                          </span>
                        </div>
                        {record.ttl !== undefined && (
                          <div>
                            <span className="text-gray-600">TTL:</span>
                            <span className="ml-2 font-medium">{record.ttl}s</span>
                          </div>
                        )}
                        {record.priority !== undefined && (
                          <div>
                            <span className="text-gray-600">Priority:</span>
                            <span className="ml-2 font-medium">{record.priority}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Common DNS Record Types</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>A Record:</strong> Maps domain to IPv4 address</li>
                <li>• <strong>AAAA Record:</strong> Maps domain to IPv6 address</li>
                <li>• <strong>CNAME Record:</strong> Creates alias pointing to another domain</li>
                <li>• <strong>MX Record:</strong> Specifies mail servers for domain</li>
                <li>• <strong>TXT Record:</strong> Stores arbitrary text data</li>
                <li>• <strong>NS Record:</strong> Specifies authoritative name servers</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">DNS Troubleshooting Tips</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• <strong>Check A Records:</strong> Ensure domain points to correct IP</li>
                <li>• <strong>Verify MX Records:</strong> Confirm email routing is configured</li>
                <li>• <strong>Check TTL Values:</strong> Low TTL allows faster propagation</li>
                <li>• <strong>Monitor NS Records:</strong> Ensure nameservers are correct</li>
                <li>• <strong>Test from Multiple Locations:</strong> DNS may vary by location</li>
                <li>• <strong>Wait for Propagation:</strong> Changes can take up to 48 hours</li>
              </ul>
            </div>
          </div>

          {/* Technical Information */}
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 className="font-semibold text-yellow-900 mb-2">How DNS Works</h4>
            <div className="text-sm text-yellow-800 space-y-2">
              <p>
                <strong>Domain Name System (DNS)</strong> translates human-readable domain names into IP addresses that computers use to communicate.
              </p>
              <p>
                When you visit a website, your computer queries DNS servers to find the IP address associated with the domain name, enabling your browser to connect to the correct server.
              </p>
              <p>
                DNS uses a hierarchical system with multiple record types to store different kinds of information about domains, from basic IP addresses to email routing and security policies.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DNSLookupTool;
